// Personalization definitions: create a versioned field set for an approved
// concept (prefilled with the Book Club fields), review it, and browse versions.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BOOK_CLUB_PERSONALIZATION_FIELDS } from '@commerce/shared';
import { api, ApiClientError } from '../../lib/api.js';
import type { Concept, PersonalizationDef } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function PersonalizationPage() {
  const queryClient = useQueryClient();
  const [conceptId, setConceptId] = useState('');
  const [name, setName] = useState('Book Club Mug');
  const [fieldsJson, setFieldsJson] = useState(
    JSON.stringify(BOOK_CLUB_PERSONALIZATION_FIELDS, null, 2),
  );
  const [error, setError] = useState<string | null>(null);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', 'approved'],
    queryFn: () => api.get<Concept[]>('/concepts?status=approved&pageSize=100'),
  });
  const listQuery = useQuery({
    queryKey: ['personalization-defs'],
    queryFn: () => api.get<PersonalizationDef[]>('/personalization-defs?pageSize=100'),
  });

  const create = useMutation({
    mutationFn: () => {
      if (!conceptId) throw new Error('Select an approved concept');
      let fields: unknown;
      try {
        fields = JSON.parse(fieldsJson);
      } catch {
        throw new Error('Fields must be valid JSON');
      }
      return api.post('/personalization-defs', { conceptId, name, fields });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['personalization-defs'] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'archived' }) =>
      api.post(`/personalization-defs/${id}/review`, { decision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personalization-defs'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const concepts = conceptsQuery.data?.data ?? [];
  const defs = listQuery.data?.data ?? [];

  return (
    <section>
      <h1>Personalization</h1>
      <p className="muted">
        Define the editable text fields customers can personalize. Input is always treated as data,
        never markup.
      </p>

      <div className="card">
        <h2>New definition</h2>
        <div className="form-row">
          <label htmlFor="pz-concept">Approved concept</label>
          <select id="pz-concept" value={conceptId} onChange={(e) => setConceptId(e.target.value)}>
            <option value="">Select a concept…</option>
            {concepts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="pz-name">Name</label>
          <input id="pz-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-row">
          <label htmlFor="pz-fields">Fields (JSON)</label>
          <textarea
            id="pz-fields"
            rows={16}
            className="mono"
            value={fieldsJson}
            onChange={(e) => setFieldsJson(e.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="button" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create definition'}
        </button>
      </div>

      <div className="card">
        <h2>Definitions</h2>
        {defs.length === 0 && <p className="muted">None yet.</p>}
        <table className="list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Fields</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {defs.map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td>v{d.version}</td>
                <td>{d.fields.map((f) => f.fieldKey).join(', ')}</td>
                <td>
                  <StatusBadge status={d.status} />
                </td>
                <td>
                  {d.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => review.mutate({ id: d.id, decision: 'approved' })}
                    >
                      Approve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
